import { LitElement, html, nothing } from 'lit';
import type { ActorQuestionAnswer } from '../types';
import { currentLang } from '../i18n';
import { actorAnswerOptionKey } from '../goals-actions';
import { defineZenComponent } from './register';

export type ActorJudgmentAnswerDetail = {
  goalId: string;
  conditionRef: string;
  actor: 'principal' | 'teammate' | 'external_party' | 'hold';
  teammateUserId?: string;
};

/**
 * #2732 (owner decision 2026-10-19): 「誰が進めますか」 as a judgment, in the
 * same card as the others. The answer stays checked, can be chosen again, and
 * 「保留にする」 withdraws it.
 */
export class ZenActorJudgmentPanel extends LitElement {
  static properties = {
    goalId: { type: String },
    conditionRef: { attribute: false },
    label: { attribute: false },
    answer: { attribute: false },
    teammates: { attribute: false },
    pendingOptionKey: { attribute: false },
  };

  declare goalId: string;
  declare conditionRef: string;
  /** The state the condition reaches, e.g. 「日程の返信が届いている」. */
  declare label: string;
  declare answer: ActorQuestionAnswer | null;
  declare teammates: Array<{ user_id: string; name: string }>;
  /** The answer just given ('' for a hold), shown while it is saved and read back. */
  declare pendingOptionKey: string | null;

  constructor() {
    super();
    this.goalId = '';
    this.conditionRef = '';
    this.label = '';
    this.answer = null;
    this.teammates = [];
    this.pendingOptionKey = null;
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.style.display = 'contents';
  }

  private emitAnswer(detail: Omit<ActorJudgmentAnswerDetail, 'goalId' | 'conditionRef'>) {
    if (this.pendingOptionKey !== null) return;
    this.dispatchEvent(new CustomEvent<ActorJudgmentAnswerDetail>('actor-judgment-answer', {
      bubbles: true,
      composed: true,
      detail: { goalId: this.goalId, conditionRef: this.conditionRef, ...detail },
    }));
  }

  render() {
    const en = currentLang === 'en';
    const pending = this.pendingOptionKey !== null;
    const selected = pending ? this.pendingOptionKey : actorAnswerOptionKey(this.answer);
    const options: Array<{ key: string; label: string; detail: Omit<ActorJudgmentAnswerDetail, 'goalId' | 'conditionRef'> }> = [
      { key: 'principal', label: en ? 'Me (with the AI)' : '自分（AIと進める）', detail: { actor: 'principal' } },
      ...this.teammates.map((member) => ({
        key: `teammate:${member.user_id}`,
        label: member.name,
        detail: { actor: 'teammate' as const, teammateUserId: member.user_id },
      })),
      { key: 'external_party', label: en ? 'The other side' : '相手側', detail: { actor: 'external_party' } },
    ];
    const question = en
      ? (this.label ? `Who carries out "${this.label}"?` : 'Who carries this out?')
      : (this.label ? `「${this.label}」は誰が進めますか？` : '誰が進めますか？');
    return html`
      <section class="canonical-judgment-panel" aria-label=${en ? 'Choose who carries it out' : '誰が進めるかを選ぶ'}>
        <div class="canonical-judgment-heading">
          <span class="mi mi-sm">gavel</span>
          <span>${en ? 'Your decision' : 'あなたの判断'}</span>
        </div>
        <p class="canonical-judgment-question">${question}</p>
        <div class="canonical-judgment-options" role="group">
          ${options.map((option) => html`
            <button
              type="button"
              class=${`canonical-judgment-option${selected === option.key ? ' selected' : ''}`}
              ?disabled=${pending}
              aria-pressed=${selected === option.key ? 'true' : 'false'}
              @click=${() => this.emitAnswer(option.detail)}
            >
              <span class="canonical-judgment-option-label">${option.label}</span>
              ${selected === option.key ? html`<span class="mi mi-xs" aria-label=${en ? 'Selected' : '選択済み'}>check_circle</span>` : nothing}
            </button>
          `)}
        </div>
        ${!pending && this.answer ? html`
          <button type="button" class="canonical-judgment-hold" @click=${() => this.emitAnswer({ actor: 'hold' })}>
            <span class="mi mi-sm">pause_circle</span>
            <span>${en ? 'Put on hold' : '保留にする'}</span>
          </button>
        ` : nothing}
      </section>
    `;
  }
}

defineZenComponent('zen-actor-judgment-panel', ZenActorJudgmentPanel);
